<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { GDialog } from 'gitart-vue-dialog';
import Api from '@/services/api/ApiServiceFabrick';
import { CSV_DATA_EXPORT_FORMATS } from '@/services/consts';
import { formatDate } from '@/services/functions';
import RangeDatePicker from '@/components/Common/RangeDatePicker.vue';
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import i18n from '@/lang/i18n';

const title = i18n.global.t('pages.csvDataExport.title.csvDataExport');

const form = ref({
  format: CSV_DATA_EXPORT_FORMATS[0].value,
  operationOffice: null as any,
  selectDateRange: [new Date(), new Date()] as Date[],
});

const formats = CSV_DATA_EXPORT_FORMATS.map((f) => ({ title: f.title, value: f.value }));
const errors = ref<Record<string, string[]>>({});
const dialogState = ref(false);
const submitting = ref(false);

const submitBtnLabel = computed(() =>
  submitting.value
    ? i18n.global.t('common.downloadingBtn')
    : i18n.global.t('common.downloadBtn')
);

onMounted(() => {
  document.title = title;
});

function isValid(): boolean {
  errors.value = {};
  if (!form.value.operationOffice) {
    errors.value.operationOffice = [i18n.global.t('pages.csvDataExport.messages.operationOfficeRequired')];
  }
  if (!form.value.selectDateRange[0] || !form.value.selectDateRange[1]) {
    errors.value.end_date = [i18n.global.t('pages.csvDataExport.messages.dateRangeRequired')];
  }
  return Object.keys(errors.value).length === 0;
}

function handleClickOutput(): void {
  if (!isValid()) {
    return;
  }

  submitting.value = true;

  const startDate = form.value.selectDateRange[0];
  const endDate = form.value.selectDateRange[1];

  const params = {
    type: form.value.format,
    operation_office_id: form.value.operationOffice.id as number,
    start_date: formatDate(startDate),
    end_date: formatDate(endDate),
  };

  Api().createCsvDataExport(params)
    .then(() => {
      dialogState.value = true;
      submitting.value = false;
    })
    .catch((error) => {
      const res = error.response;
      switch (res?.status) {
        case 401:
        case 403:
          location.reload();
          break;
        case 422:
          errors.value = res.data.errors;
          break;
      }
      submitting.value = false;
    });
}
</script>

<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <h4 class="card-title mt-0">{{ title }}</h4>
      </div>
      <div class="row">
        <div class="col-3 mt-2">
          <v-select
            class="small mt-2"
            v-model="form.format"
            :items="formats"
            :label="i18n.global.t('pages.csvDataExport.format')"
            :clearable="false"
          />
        </div>
        <div class="col-3 mt-2">
          <SingleOperationOfficeAutoComplete
            v-model="form.operationOffice"
            class="mt-2"
            :selectFirstIfOnlyOne="true"
            :clearable="false"
            :error-messages="errors.operationOffice"
          />
        </div>
        <div class="col-4">
          <span class="small">
            {{ i18n.global.t('pages.csvDataExport.range') }}
          </span>
          <ValidationErrors :messages="errors.end_date">
            <RangeDatePicker
              v-model="form.selectDateRange"
              :maxRange="30"
              format="yyyy/MM/dd"
              :isError="!!errors.end_date"
            />
          </ValidationErrors>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3">
        <v-btn
          class="exec-btn"
          @click="handleClickOutput"
          :style="{ backgroundColor: submitting ? '#B3B8BB' : '' }"
        >
          {{ submitBtnLabel }}
        </v-btn>
      </div>
    </div>
  </div>
  <GDialog
    v-model="dialogState"
    max-width="30%"
    transition="custom-from-bottom-transition"
  >
    <div class="modal-header justify-content-center pt-3">
      <label>{{ i18n.global.t('pages.csvDataExport.completedReception') }}</label>
    </div>
    <div class="row mt-2 mr-3 ml-3 pl-5 v-theme--light">
      {{ i18n.global.t('pages.csvDataExport.messages.acceptedDownload') }}
      <br />
      {{ i18n.global.t('pages.csvDataExport.messages.outputCompletedAfterEmailNotification') }}
    </div>
    <div class="d-flex justify-content-end m-3">
      <v-btn class="m-1 reset-btn" @click="dialogState = false">
        {{ i18n.global.t('pages.common.button.close') }}
      </v-btn>
    </div>
  </GDialog>
</template>
